import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Capacitor, registerPlugin } from '@capacitor/core';

import { useAuth } from './AuthContext';
import { db } from '../services/supabase';

const ParentThemeContext = createContext(null);

const nativeSystemBars = registerPlugin('MyPiggySystemBars');

const STORAGE_KEY = 'mypiggy-parent-theme';

export const PARENT_THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
};

const normalizeTheme = (value) =>
  value === PARENT_THEMES.DARK
    ? PARENT_THEMES.DARK
    : PARENT_THEMES.LIGHT;

const readCachedTheme = () => {
  if (typeof window === 'undefined') {
    return PARENT_THEMES.LIGHT;
  }

  return normalizeTheme(
    window.localStorage.getItem(STORAGE_KEY)
  );
};

const cacheTheme = (theme) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    STORAGE_KEY,
    normalizeTheme(theme)
  );
};

export const ParentThemeProvider = ({ children }) => {
  const { parent } = useAuth();

  const [theme, setTheme] = useState(readCachedTheme);
  const [savingTheme, setSavingTheme] = useState(false);
  const [themeError, setThemeError] = useState('');

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android' ||
        !Capacitor.isPluginAvailable('MyPiggySystemBars')) {
      return;
    }
    nativeSystemBars.setTheme({ theme }).catch((error) => {
      console.warn('Unable to update Android system bar appearance:', error);
    });
  }, [theme]);

  /*
   * Supabase is the source of truth once the authenticated
   * parent profile has loaded. localStorage is only a fast
   * device-side cache so the last-used theme can appear before
   * the parent profile request finishes.
   */
  useEffect(() => {
    if (!parent) {
      return;
    }

    const parentTheme = normalizeTheme(
      parent.theme_preference
    );

    setTheme(parentTheme);
    cacheTheme(parentTheme);
  }, [parent?.id, parent?.theme_preference]);

  const updateTheme = useCallback(async (nextTheme) => {
    const normalizedTheme = normalizeTheme(nextTheme);
    const previousTheme = theme;

    setThemeError('');
    setTheme(normalizedTheme);
    cacheTheme(normalizedTheme);

    if (!parent?.id) {
      return;
    }

    try {
      setSavingTheme(true);

      await db.updateParentTheme(
        normalizedTheme
      );
    } catch (error) {
      console.error(
        'Unable to update parent theme:',
        error
      );

      setTheme(previousTheme);
      cacheTheme(previousTheme);

      const message =
        error?.message ||
        'Unable to save your theme preference. Please try again.';

      setThemeError(message);
      throw error;
    } finally {
      setSavingTheme(false);
    }
  }, [parent?.id, theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === PARENT_THEMES.DARK,
      savingTheme,
      themeError,
      updateTheme,
    }),
    [theme, savingTheme, themeError, updateTheme]
  );

  return (
    <ParentThemeContext.Provider value={value}>
      {children}
    </ParentThemeContext.Provider>
  );
};

export const useParentTheme = () => {
  const context = useContext(ParentThemeContext);

  if (!context) {
    throw new Error(
      'useParentTheme must be used within ParentThemeProvider'
    );
  }

  return context;
};
